import { useEffect } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { trackServerPageView, trackButtonClick, setupPageTimeTracking } from "../lib/analytics";

export default function About() {
  useEffect(() => {
    trackServerPageView("/about", "about_page_view");
    return setupPageTimeTracking("/about");
  }, []);

  return (
    <>
      <SEO
        title="Transparent Pharmaceutical Wholesale"
        description="NABP-accredited, DSCSA-compliant pharmaceutical wholesaler. Founded by Jacob Fuchs, pharmacist + engineer. Serving 500+ pharmacies with transparent pricing."
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "About", path: "/about" },
        ]}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: "About Premier Pharma",
            url: "https://premierpharma.com/about",
            about: { "@id": "https://premierpharma.com/#organization" },
            isPartOf: { "@id": "https://premierpharma.com/#website" },
          },
          {
            "@context": "https://schema.org",
            "@type": "Person",
            name: "Jacob Fuchs",
            jobTitle: "Founder & CEO",
            url: "https://premierpharma.com/about",
            image: "https://premierpharma.com/jacob-fuchs-ibt.jpg",
            sameAs: [
              "https://www.youtube.com/@jacobfuchs",
              "https://www.ibtimes.com/jacob-fuchs-software-engineerpharmacist-shaping-connected-healthcare-vision-3795303",
              "https://www.medicaldaily.com/incentive-problem-healthcare-jacob-fuchss-journey-fix-it-474505",
            ],
            worksFor: {
              "@type": "Organization",
              "@id": "https://premierpharma.com/#organization",
              name: "Premier Pharma",
            },
            description: "Pharmacist and software engineer, founder of Premier Pharma, a NABP-accredited wholesale pharmaceutical distributor.",
          },
        ]}
      />

      <section className="relative py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-[#0a1e4a] to-[#0d2d6b]"></div>
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "url('/hero-bg-pattern.webp')", backgroundSize: "cover", backgroundPosition: "center" }}></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block bg-accent/20 text-accent-300 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-6">About Us</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight">
              Premier Pharma:<br />
              <span className="text-accent-300">Transparent Pharmaceutical Wholesale</span>
            </h1>
            <p className="text-lg text-white/60 mt-2">Promoting Transparency in Healthcare</p>
            <p className="mt-6 text-lg lg:text-xl text-white/70 max-w-2xl leading-relaxed">
              More than a wholesaler — a partner. We bring clarity to the pharmaceutical supply chain with sincere communication, competitive pricing, and full regulatory compliance.
            </p>
          </div>

          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 border-t border-white/10 pt-10">
            {[
              { value: "500+", label: "Partners Served" },
              { value: "5,000+", label: "Products Available" },
              { value: "100%", label: "DSCSA Compliant" },
              { value: "24/7", label: "Customer Support" },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-white">{stat.value}</div>
                <div className="text-sm text-white/60 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block bg-accent/10 text-accent text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-6">Our Mission</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-primary leading-tight mb-6">
                Built on integrity.<br />Driven by partnership.
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                The pharmaceutical industry has an incentive problem. When the people selling you drugs make more money the more you pay, something is fundamentally misaligned. We built Premier Pharma to fix that.
              </p>
              <p className="text-gray-600 leading-relaxed">
                We position ourselves around helping independent pharmacies access medications and supplies in a way that is cost-conscious and service-oriented — with transparent pricing, attentive support, and a focus on helping smaller organizations remain competitive.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {[
                {
                  title: "Transparent Pricing",
                  desc: "Clear, competitive pricing with no hidden fees or markup surprises.",
                  icon: (
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  ),
                },
                {
                  title: "Full Compliance",
                  desc: "NABP accredited, BBB accredited, DEA licensed, and 100% DSCSA compliant.",
                  icon: (
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                    </svg>
                  ),
                },
                {
                  title: "Dedicated Support",
                  desc: "Your personal account manager ensures every order is handled with care.",
                  icon: (
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                    </svg>
                  ),
                },
                {
                  title: "Free DSCSA Software",
                  desc: "Compliance platform included at no cost with qualifying purchases.",
                  icon: (
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25" />
                    </svg>
                  ),
                },
              ].map((item, i) => (
                <div key={i} className="bg-gray-50 rounded-2xl p-5 border border-gray-100 hover:border-accent/20 hover:shadow-sm transition-all">
                  <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center text-accent mb-3">
                    {item.icon}
                  </div>
                  <h3 className="font-semibold text-primary text-sm mb-1">{item.title}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12 items-start">
            <div className="lg:col-span-2 flex flex-col items-center lg:items-start">
              <div className="rounded-2xl overflow-hidden shadow-xl ring-4 ring-white w-full max-w-sm">
                <img src="/jacob-fuchs-ibt.jpg" alt="Jacob Fuchs, Founder of Premier Pharma" className="w-full h-auto" loading="lazy" />
              </div>
              <div className="mt-5 text-center lg:text-left">
                <h3 className="text-xl font-bold text-primary">Jacob Fuchs</h3>
                <p className="text-sm text-gray-500 mt-0.5">Founder, Premier Pharma</p>
                <a href="https://www.youtube.com/@jacobfuchs" target="_blank" rel="noopener noreferrer" onClick={() => trackButtonClick("The Pharma Problem Podcast", "founder-section")} className="inline-flex items-center gap-1.5 mt-3 text-sm text-accent hover:text-accent-600 font-medium transition-colors">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814z"/><path fill="#fff" d="M9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                  The Pharma Problem Podcast
                </a>
              </div>
            </div>

            <div className="lg:col-span-3">
              <span className="inline-block bg-accent/10 text-accent text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-6">Our Founder</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-primary leading-tight mb-6">
                Bridging Software<br />& Pharmacy
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Jacob Fuchs bridges the rarely intersecting worlds of software engineering and pharmacy. His career grew out of early exposure to healthcare operations and a persistent curiosity about how systems work beneath the surface. Today, he focuses on building healthcare infrastructure that is both adaptable and supportive of the people who depend on it.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                In parallel with his entrepreneurial work, Fuchs holds senior leadership responsibilities at a not-for-profit health system, overseeing multiple operational areas and engaging with purchasing and coordination at scale. This dual perspective supports his ability to move between strategic planning and day-to-day operations.
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                A personal influence on his outlook traces back to adolescence, when he was diagnosed with type 1 diabetes. Early use of emerging sensor-based technologies offered firsthand insight into both the promise and the constraints of modern care — deepening his empathy for patients and sharpening his awareness of how systems shape daily realities.
              </p>

              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                    <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 24 24"><path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/></svg>
                  </div>
                  <div>
                    <p className="text-gray-700 italic leading-relaxed">
                      Systems follow the paths they're designed to reward. Change the design, and behavior evolves with it.
                    </p>
                    <p className="mt-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">As quoted in Medical Daily</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block bg-accent/10 text-accent text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">What We Stand For</span>
            <h2 className="text-3xl lg:text-4xl font-bold text-primary">Our Values</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: "Innovation",
                desc: "Life is complicated enough. We strive to make things simpler and better, cultivating curiosity and rewarding competitive urgency.",
                icon: (
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
                  </svg>
                ),
                color: "amber",
              },
              {
                title: "Transparency",
                desc: "We've been clear about our objective since day one: reducing costs by bringing clarity to healthcare with sincere communication.",
                icon: (
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                ),
                color: "blue",
              },
              {
                title: "Integrity",
                desc: "Do the right thing. We're committed to performing all tasks with personal, thoughtful care for the greater good.",
                icon: (
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.05 4.575a1.575 1.575 0 10-3.15 0v3m3.15-3v-1.5a1.575 1.575 0 013.15 0v1.5m-3.15 0l.075 5.925m3.075.75V4.575m0 0a1.575 1.575 0 013.15 0V15M6.9 7.575a1.575 1.575 0 10-3.15 0v8.175a6.75 6.75 0 006.75 6.75h2.018a5.25 5.25 0 003.712-1.538l1.732-1.732a5.25 5.25 0 001.538-3.712l.003-2.024a.668.668 0 01.198-.471 1.575 1.575 0 10-2.228-2.228 3.818 3.818 0 00-1.12 2.687M6.9 7.575V12m6.27 4.318A4.49 4.49 0 0116.35 15m.002 0h-.002" />
                  </svg>
                ),
                color: "green",
              },
              {
                title: "Quality",
                desc: "We go above and beyond in everything we do — from the products we supply to the relationships we build.",
                icon: (
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
                  </svg>
                ),
                color: "purple",
              },
            ].map((v, i) => (
              <div key={i} className="text-center p-6 rounded-2xl bg-gray-50 border border-gray-100 hover:border-accent/20 hover:shadow-sm transition-all">
                <div className={`w-12 h-12 rounded-xl mx-auto mb-4 flex items-center justify-center ${
                  v.color === "amber" ? "bg-amber-100 text-amber-600" :
                  v.color === "blue" ? "bg-blue-100 text-blue-600" :
                  v.color === "green" ? "bg-green-100 text-green-600" :
                  "bg-purple-100 text-purple-600"
                }`}>
                  {v.icon}
                </div>
                <h3 className="text-lg font-bold text-primary mb-2">{v.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 bg-gray-50 border-y border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-6 sm:gap-10 flex-wrap">
            <span className="text-xs text-gray-400 uppercase tracking-wider font-medium">Trusted & Accredited</span>
            <a href="https://mybadges.us.openbadges.me/api/badgeitem/c44016ac-de61-418c-951f-86c0cddd258b/metadata" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <img src="/nabp-badge.png" alt="NABP Accredited Drug Distributor" className="w-8 h-8 object-contain" loading="lazy" />
              <span className="text-sm font-semibold text-primary">NABP Accredited</span>
            </a>
            <a href="https://www.bbb.org/us/id/boise/profile/pharmaceutical-supplies/premier-pharma-1296-1000131516" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <img src="https://seal-alaskaoregonwesternwashington.bbb.org/logo/rbhzbul/bbb-1000131516.png" alt="BBB Accredited Business" className="h-8 object-contain" loading="lazy" />
              <span className="text-sm font-semibold text-primary">BBB Accredited</span>
            </a>
            <a href="https://www.ibtimes.com/jacob-fuchs-software-engineerpharmacist-shaping-connected-healthcare-vision-3795303" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:opacity-80 transition-opacity group">
              <span className="font-bold text-primary text-sm tracking-wide">IBT</span>
              <svg className="w-3.5 h-3.5 text-gray-400 group-hover:text-accent transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
            </a>
            <a href="https://www.medicaldaily.com/incentive-problem-healthcare-jacob-fuchss-journey-fix-it-474505" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:opacity-80 transition-opacity group">
              <span className="font-bold text-primary text-sm tracking-wide">Medical Daily</span>
              <svg className="w-3.5 h-3.5 text-gray-400 group-hover:text-accent transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
            </a>
          </div>
        </div>
      </section>

      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-[#0a1e4a] to-accent/80"></div>
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-5xl font-bold text-white mb-6 leading-tight">
            Ready to partner<br />with Premier Pharma?
          </h2>
          <p className="text-white/75 text-lg max-w-2xl mx-auto mb-8">
            Join hundreds of pharmacies nationwide who trust us for competitive pricing, regulatory compliance, and dedicated service. Create your account and start ordering in minutes.
          </p>
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-5 py-2 mb-8">
            <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
            <span className="text-sm text-white/90 font-medium">Instant approval with valid credentials</span>
          </div>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/register" onClick={() => trackButtonClick("Create Account — Get Approved Instantly", "bottom-cta")} className="bg-white text-primary rounded-btn px-8 py-4 font-semibold text-lg hover:bg-gray-100 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5">
              Create Account — Get Approved Instantly
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl border border-gray-200 p-8 lg:p-12">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <span className="inline-block bg-accent/10 text-accent text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">Affiliate Program</span>
                <h2 className="text-3xl font-bold text-primary leading-tight mb-4">
                  Earn by Referring Pharmacies
                </h2>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Join the ScriptTrack Affiliate Program and earn tiered commissions by helping healthcare providers modernize. Grow your earnings as you grow your referrals.
                </p>
                <div className="grid grid-cols-3 gap-4 mb-8">
                  {[
                    { tier: "Starter", rate: "1%" },
                    { tier: "Growth", rate: "2%" },
                    { tier: "Partner", rate: "3%" },
                  ].map((t, i) => (
                    <div key={i} className="text-center bg-white rounded-xl border border-gray-100 p-3">
                      <div className="text-2xl font-bold text-accent">{t.rate}</div>
                      <div className="text-xs text-gray-500 font-medium mt-1">{t.tier}</div>
                    </div>
                  ))}
                </div>
                <a
                  href="https://premierscripttrack.com/affiliate-signup"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackButtonClick("Apply to Become an Affiliate", "affiliate-section")}
                  className="inline-flex items-center gap-2 bg-accent hover:bg-accent-600 text-white rounded-btn px-6 py-3 font-semibold transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5"
                >
                  Apply to Become an Affiliate
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                </a>
              </div>
              <div className="space-y-4">
                {[
                  { icon: "💰", text: "Recurring monthly commissions" },
                  { icon: "♾️", text: "No limit on referrals" },
                  { icon: "🤝", text: "Dedicated support team" },
                  { icon: "📊", text: "Real-time earnings dashboard" },
                  { icon: "📈", text: "+ 10% of monthly software fees" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 bg-white rounded-xl border border-gray-100 p-4">
                    <span className="text-xl" aria-hidden="true">{item.icon}</span>
                    <span className="text-sm font-medium text-gray-700">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
